import React, { useEffect, useState, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { createSlotFromSession } from '../../utils/transformSessionDateToSlot';
import { formatISODateToDDMMYYYY } from '@/utils';
import {
	Clock,
	CheckCircle2,
	PlayCircle,
	CheckSquare,
	XCircle,
	ChevronDown,
	User,
	RefreshCcw,
	CalendarClock,
	Check,
	Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useModal } from '@/hooks';
import {
	useGetAvailableDate,
	usePatchSessionDate,
	usePatchSessionStatus,
	usePatchSessionMetadata,
} from '@/services';

import { AvailableDatePicker, Button, CopyWrapper } from '@/components';

// --- MAPEAMENTOS ---
const SESSION_STATUS_INFO: Record<
	string,
	{ label: string; icon: React.ReactNode }
> = {
	CREATED: { label: 'CRIADA', icon: <Clock size={12} /> },
	CONFIRMED: { label: 'CONFIRMADA', icon: <CheckCircle2 size={12} /> },
	STARTED: { label: 'EM ANDAMENTO', icon: <PlayCircle size={12} /> },
	FINISHED: { label: 'FINALIZADA', icon: <CheckSquare size={12} /> },
	CANCELLED: { label: 'CANCELADA', icon: <XCircle size={12} /> },
};

const SESSION_TYPE_INFO: Record<
	string,
	{ label: string; icon: React.ReactNode }
> = {
	DEFAULT: { label: 'INDIVIDUAL', icon: <User size={12} /> },
	RETURN: { label: 'RETORNO', icon: <RefreshCcw size={12} /> },
	GROUP: { label: 'EM GRUPO', icon: <User size={12} /> },
};

function getPrimaryParticipantName(session: Sessions.Session): string {
	const user =
		session.user ??
		(
			session as {
				participants?: {
					user: { first_name?: string; last_name?: string };
				}[];
			}
		).participants?.[0]?.user;
	return user
		? `${user.first_name ?? ''} ${(user.last_name ?? '').trim()}`.trim() ||
				'—'
		: '—';
}

const ReceiptLabel = ({ title }: { title: string }) => (
	<h3 className="mb-2 text-[10px] font-bold uppercase tracking-wider text-stone-400">
		{title}
	</h3>
);

// --- COMPONENTE DE DROPDOWN ---
interface PortalDropdownProps {
	triggerContent: React.ReactNode;
	options: Array<{
		key: string;
		label: string;
		icon?: React.ReactNode;
		onClick: () => void;
	}>;
	scrollable?: boolean;
}

const GenericPortalDropdown = ({
	triggerContent,
	options,
	scrollable,
}: PortalDropdownProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [coords, setCoords] = useState({
		top: 0,
		left: 0,
		width: 0,
		openUp: false,
	});
	const triggerRef = useRef<HTMLButtonElement>(null);
	const menuRef = useRef<HTMLDivElement>(null);

	const toggle = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (isOpen) {
			setIsOpen(false);
			return;
		}
		if (triggerRef.current) {
			const rect = triggerRef.current.getBoundingClientRect();
			const menuHeight = scrollable ? 180 : options.length * 40;
			const openUp =
				window.innerHeight - rect.bottom < menuHeight &&
				rect.top > menuHeight;

			setCoords({
				top: openUp
					? rect.top + window.scrollY - 4
					: rect.bottom + window.scrollY + 4,
				left: rect.left + window.scrollX,
				width: rect.width,
				openUp,
			});
			setIsOpen(true);
		}
	};

	useEffect(() => {
		if (isOpen) {
			const handleClose = (e: Event) => {
				if (
					e.target instanceof Node &&
					menuRef.current?.contains(e.target)
				) {
					return;
				}
				setIsOpen(false);
			};

			document.addEventListener('mousedown', handleClose);
			window.addEventListener('scroll', handleClose, true);

			return () => {
				document.removeEventListener('mousedown', handleClose);
				window.removeEventListener('scroll', handleClose, true);
			};
		}
	}, [isOpen]);

	return (
		<div className="w-full min-w-0">
			<button
				ref={triggerRef}
				onClick={toggle}
				type="button"
				className="box-border flex w-full items-center justify-between gap-1 rounded-lg border border-solid border-zinc-200 bg-white px-3 py-2 text-[11px] font-bold uppercase text-stone-700 shadow-sm outline-none transition-all hover:border-zinc-300"
			>
				<div className="flex min-w-0 flex-1 items-center gap-1.5 truncate">
					{triggerContent}
				</div>
				<ChevronDown
					size={12}
					className={cn(
						'shrink-0 text-zinc-400 transition-transform',
						isOpen && 'rotate-180',
					)}
				/>
			</button>

			{isOpen &&
				createPortal(
					<div
						ref={menuRef}
						className={cn(
							'animate-in fade-in zoom-in-95 fixed z-[99999] overflow-x-hidden rounded-xl border border-solid border-zinc-200 bg-white py-1 shadow-2xl duration-100',
							scrollable
								? 'max-h-[160px] overflow-y-auto'
								: 'overflow-hidden',
						)}
						style={{
							top: coords.openUp ? 'auto' : coords.top,
							bottom: coords.openUp
								? window.innerHeight -
								  (coords.top - window.scrollY) +
								  8
								: 'auto',
							left: coords.left,
							width: coords.width,
						}}
						onMouseDown={(e) => e.stopPropagation()}
					>
						{options.map((opt) => (
							<button
								key={opt.key}
								type="button"
								onClick={() => {
									opt.onClick();
									setIsOpen(false);
								}}
								className="flex w-full items-center gap-3 border-none px-4 py-2 text-left text-[10px] font-bold uppercase text-stone-500 outline-none hover:bg-zinc-50"
							>
								{opt.icon && (
									<span className="shrink-0 opacity-70">
										{opt.icon}
									</span>
								)}
								<span className="truncate">{opt.label}</span>
							</button>
						))}
					</div>,
					document.body,
				)}
		</div>
	);
};

// --- MODAL ---
const EditSessionModal = ({ session }: { session: Sessions.Session }) => {
	const getSessionType = (sess: Sessions.Session) => {
		if (sess.type === 'GROUP') return 'GROUP';
		if (sess.type === 'RETURN') return 'RETURN';
		return 'DEFAULT';
	};

	// ESTADOS LOCAIS
	const [localStatus, setLocalStatus] = useState<string>(session.status);
	const [localType, setLocalType] = useState<string>(getSessionType(session));
	const [defaultSlot, setDefaultSlot] = useState(
		createSlotFromSession(session),
	);
	const [availableSlots, setAvailableSlots] = useState<
		AvailableDates.SessionTimeSlot[]
	>([]);
	const [editedDate, setEditedDate] = useState({
		available_date_id: session.available_date_id,
		selected_date: session.selected_date,
	});

	// SINCRONIZAÇÃO
	useEffect(() => {
		setLocalStatus(session.status);
		setLocalType(getSessionType(session));
		setDefaultSlot(createSlotFromSession(session));
		setEditedDate({
			available_date_id: session.available_date_id,
			selected_date: session.selected_date,
		});
	}, [session.id, session.status, session.metadata]);

	const { closeModal } = useModal();
	const { professional } = session;
	const { data: availabilityData, isLoading } = useGetAvailableDate(
		professional.id,
	);
	const { mutate: mutateSessionDate, isPending: isSavingDate } =
		usePatchSessionDate(closeModal);
	const { mutate: mutateSessionStatus } = usePatchSessionStatus();
	const { mutate: mutateSessionMetadata } = usePatchSessionMetadata();

	useEffect(() => {
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = 'auto';
		};
	}, []);

	const allHours = useMemo(() => {
		const now = new Date();
		return (
			availabilityData?.professionalAvailability.availability
				.flatMap((day) => day.hours)
				.filter((hour) => new Date(hour.start_time) > now) || []
		);
	}, [availabilityData]);

	const localAvailableDates = useMemo(() => {
		const localDates = allHours.map((hour) => {
			const d = new Date(hour.start_time);
			return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
				2,
				'0',
			)}-${String(d.getDate()).padStart(2, '0')}`;
		});
		return Array.from(new Set(localDates));
	}, [allHours]);

	const getSlotsForLocalDate = (date: string) => {
		const [day, month, year] = date.split('/');
		const formattedDate = `${year}-${month}-${day}`;
		return allHours.filter((hour) => {
			const d = new Date(hour.start_time);
			return (
				`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
					2,
					'0',
				)}-${String(d.getDate()).padStart(2, '0')}` === formattedDate
			);
		});
	};

	useEffect(() => {
		if (availabilityData) {
			const slots = getSlotsForLocalDate(
				formatISODateToDDMMYYYY(session.selected_date),
			);
			setAvailableSlots(slots);
		}
	}, [availabilityData, session.selected_date]);

	return (
		<div className="space-y-6">
			<div className="flex flex-col gap-1">
				<span className="text-[11px] font-bold uppercase tracking-widest text-stone-400">
					PACIENTE:{' '}
					<span className="ml-1 font-black text-stone-600">
						{getPrimaryParticipantName(session)}
					</span>
				</span>

				<div className="flex items-center gap-2">
					<span className="shrink-0 text-[11px] font-bold uppercase tracking-widest text-stone-400">
						ID da sessão:
					</span>
					<CopyWrapper
						valueToCopy={session.id}
						wrapperClassName="min-w-0"
						buttonClassName="right-[-4px]"
					>
						<span className="block truncate rounded px-2 py-0.5 pr-8 text-[10px] font-black text-stone-500">
							#{session.id}
						</span>
					</CopyWrapper>
				</div>
			</div>

			<div className="box-border w-full space-y-6">
				{/* NOVO HORÁRIO + TIPO */}
				<div className="flex w-full min-w-0 gap-4">
					<div className="min-w-0 flex-1">
						<ReceiptLabel title="Novo horário" />
						<GenericPortalDropdown
							scrollable
							triggerContent={
								<>
									<CalendarClock
										size={12}
										className="shrink-0"
									/>
									<span className="truncate">
										{defaultSlot
											? `${String(
													new Date(
														defaultSlot.start_time,
													).getHours(),
											  ).padStart(2, '0')}:${String(
													new Date(
														defaultSlot.start_time,
													).getMinutes(),
											  ).padStart(2, '0')} - ${String(
													new Date(
														defaultSlot.end_time,
													).getHours(),
											  ).padStart(2, '0')}:${String(
													new Date(
														defaultSlot.end_time,
													).getMinutes(),
											  ).padStart(2, '0')}`
											: 'Selecione'}
									</span>
								</>
							}
							options={availableSlots.map((slot) => ({
								key: slot.available_date_id,
								label: `${String(
									new Date(slot.start_time).getHours(),
								).padStart(2, '0')}:${String(
									new Date(slot.start_time).getMinutes(),
								).padStart(2, '0')} - ${String(
									new Date(slot.end_time).getHours(),
								).padStart(2, '0')}:${String(
									new Date(slot.end_time).getMinutes(),
								).padStart(2, '0')}`,
								icon: <Clock size={12} />,
								onClick: () => {
									setDefaultSlot(slot);
									setEditedDate({
										available_date_id:
											slot.available_date_id,
										selected_date: slot.start_time,
									});
								},
							}))}
						/>
					</div>

					<div className="min-w-0 flex-1">
						<ReceiptLabel title="Tipo da consulta" />
						{localType === 'GROUP' ? (
							<div className="flex items-center gap-2 rounded-lg border border-zinc-200 bg-stone-50/50 px-3 py-2 text-[11px] font-bold text-stone-700">
								{SESSION_TYPE_INFO[localType].icon}
								<span>
									{SESSION_TYPE_INFO[localType].label}
								</span>
							</div>
						) : (
							<GenericPortalDropdown
								triggerContent={
									<>
										{SESSION_TYPE_INFO[localType].icon}
										<span className="truncate">
											{SESSION_TYPE_INFO[localType].label}
										</span>
									</>
								}
								options={Object.entries(SESSION_TYPE_INFO)
									.filter(([key]) => key !== 'GROUP')
									.map(([key, item]) => ({
										key,
										label: item.label,
										icon: item.icon,
										onClick: () => {
											setLocalType(key);
											mutateSessionMetadata({
												id: session.id,
												body: {
													metadata:
														key === 'RETURN'
															? [
																	{
																		type: 'RETURN',
																	},
															  ]
															: null,
												},
											});
										},
									}))}
							/>
						)}
					</div>
				</div>

				{/* Máx. participantes (sessão em grupo) */}
				{session.type === 'GROUP' && (
					<div className="w-full min-w-0">
						<ReceiptLabel title="Máx. participantes" />
						<div className="flex items-center gap-2 rounded-lg border border-solid border-zinc-200 bg-stone-50/50 px-3 py-2">
							<span className="text-[11px] font-bold text-stone-700">
								{session.max_participants ?? '—'}
							</span>
						</div>
					</div>
				)}

				{/* STATUS */}
				<div className="w-full min-w-0">
					<ReceiptLabel title="Status da consulta" />
					<GenericPortalDropdown
						triggerContent={
							<>
								{SESSION_STATUS_INFO[localStatus]?.icon}
								<span className="truncate">
									{SESSION_STATUS_INFO[localStatus]?.label}
								</span>
							</>
						}
						options={Object.entries(SESSION_STATUS_INFO).map(
							([key, item]) => ({
								key,
								label: item.label,
								icon: item.icon,
								onClick: () => {
									setLocalStatus(key);
									mutateSessionStatus({
										id: session.id,
										body: {
											status: key as Sessions.SessionStatus,
										},
									});
								},
							}),
						)}
					/>
				</div>

				{/* CALENDÁRIO */}
				<div className="w-full min-w-0">
					<ReceiptLabel title="Nova data" />
					<div className="w-full overflow-hidden">
						<AvailableDatePicker
							availableDates={localAvailableDates}
							onDateChange={(date) => {
								if (!date) return;
								const slots = getSlotsForLocalDate(date);
								setDefaultSlot(null);
								setAvailableSlots(slots);
								setEditedDate({
									available_date_id: '',
									selected_date: '',
								});
							}}
							loading={isLoading}
							defaultValue={session.selected_date}
							fullWidth
						/>
					</div>

					{editedDate.available_date_id &&
						editedDate.selected_date &&
						editedDate.selected_date !== session.selected_date && (
							<Button
								type="button"
								disabled={isSavingDate}
								className="mt-3 w-full items-center justify-center rounded-lg bg-stone-900 py-3 text-[10px] font-bold uppercase text-white shadow-lg hover:bg-stone-800 disabled:opacity-50"
								onClick={() =>
									mutateSessionDate({
										id: session.id,
										body: {
											selected_date:
												editedDate.selected_date,
											available_date_id:
												editedDate.available_date_id ??
												undefined,
										},
									})
								}
								text={
									isSavingDate
										? 'Salvando...'
										: 'Salvar nova data'
								}
								startIcon={
									isSavingDate ? (
										<Loader2 className="size-4 animate-spin" />
									) : (
										<Check className="size-4 stroke-[3]" />
									)
								}
							/>
						)}
				</div>
			</div>
		</div>
	);
};

export { EditSessionModal };
